import * as Sentry from '@sentry/react';

import { ServerList } from '@crossnokaye/typescript-sdk';

import { router } from '@/singletons';

import { AmplitudeProvider } from './analytics/amplitude-provider';
import { analytics } from './analytics/analytics-service';

/**
 * Options that govern the behavior of third-party observability and telemetry services.
 */
export interface InitializeOptions {
  /// True if the ATLAS environment is production; false for office, etc.
  productionDeployment: boolean;
  /// True if the UI is a compiled, production Vite build; false for dev server or (hypothetical) dev build.
  productionUI: boolean;
  /// True if the UI is running inside a test automation environment; false for normal UI usage.
  testAutomation: boolean;
}

/**
 * Initialize third-party observability and telemetry services.
 */
export function initialize(options: InitializeOptions) {
  initializeAnalyticProvider(options);
  initializeSentry(options);
}

/**
 * Initialize Analytics Provider if the UI is a production build and not running in test automation or development mode.
 */
function initializeAnalyticProvider({
  productionDeployment,
  productionUI,
  testAutomation,
}: InitializeOptions) {
  const analyticsActive = productionDeployment && productionUI && !testAutomation;

  (async () => {
    const amplitudeProvider = new AmplitudeProvider();
    await amplitudeProvider.init({
      isActive: analyticsActive,
      apiKey: '3551afe5968fd19735f4e77a10e1d9a0',
    });
    analytics.addProvider(amplitudeProvider);
  })().catch((error) => {
    console.error('Failed to initialize analytics:', error);
  });
}

/**
 * Initialize Sentry if the UI is a production build and not running in test automation.
 */
function initializeSentry({
  productionDeployment,
  productionUI,
  testAutomation,
}: InitializeOptions) {
  Sentry.init({
    dsn: 'https://a6f63c4afcf0e29864185408457b5d52@o1073571.ingest.us.sentry.io/4508757527887872',
    enabled: productionUI && !testAutomation,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', ...ServerList],
    environment: productionDeployment ? 'Production' : 'Not Production',
    release: import.meta.env.VITE_GIT_SHA || 'development',
  });
}
